// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';

// Third party packages
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from 'rxjs/operators';

// Custom packages
import { MatDialog, MatOptionSelectionChange } from '@angular/material';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { ImportPhonebookContactDialogComponent } from 'src/app/modules/companies/components/import-phonebook-dialog/import-phonebook-dialog.component';
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { AddCommunicationDialogComponent } from 'src/app/shared/components/dialogs/add-communication-dialog/add-communication-dialog.component';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { PhonebookDataSource } from 'src/app/shared/utils/data-sources/phonebook-data-source';
import { AddPhonebookContactDialogComponent } from '../../components/add-phonebook-dialog/add-phonebook-dialog.component';
import { EditPhonebookContactDialogComponent } from '../../components/edit-phonebook-dialog/edit-phonebook-dialog.component';
@Component({
  selector: 'app-phonebook',
  templateUrl: './phonebook.page.html',
  styleUrls: ['./phonebook.page.scss']
})
export class PhonebookComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  activeRoute: string;
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRoleId: string;
  loggedUserRoleType: string;
  companyId: string;
  phonebook: any;
  authorized = false;
  // cardIsLoading = true;

  // BEGIN - Delete button
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.delete'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'warn',
    spinnerColor: 'warn',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-trash',
      inline: true,
    },
  };
  // END - Delete button

  // BEGIN - Table properties
  displayedColumns: string[] = ['index', 'firstName', 'lastName', 'email', 'phone', 'mobile', 'actions'];
  dataSource: PhonebookDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  firstNameFilter: FormControl = new FormControl('');
  lastNameFilter: FormControl = new FormControl('');
  emailFilter: FormControl = new FormControl('');
  phoneFilter: FormControl = new FormControl('');
  mobileFilter: FormControl = new FormControl('');
  createdAtFilter: FormControl = new FormControl('');
  propertyFilter: FormControl = new FormControl('');
  typeFilter: FormControl = new FormControl('');
  // END - Table properties

  propertyIsLoading = false;
  availableProperties: PropertyInterface[] = [];
  selectedContactsId: string[] = [];
  selectedContacts: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,

    private companiesService: CompaniesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private cookieService: CookieService,
    private router: Router,
    private configService: ConfigService,
    private propertiesService: PropertiesService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('companies.detail.phonebookTab.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));
    this.subscriptions.push(this.authService.loggedUserRequestedRole$.subscribe(res => this.loggedUserRoleId = res));
    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleType$.subscribe(
        res => {
          this.loggedUserRoleType = res
          if ((this.loggedUserRoleType === "master" || this.loggedUserRoleType === "collaborator")) {
            this.displayedColumns = ['recipients', 'firstName', 'lastName', 'email', 'property', 'type', 'actions'];
          }
        }
      )
    );

    // This page is accessible only if user is a admin/operator/master/collaborator
    if (this.loggedUserRoleType
      && ((this.loggedUserRoleType === "admin" || this.loggedUserRoleType === "operator")
        || (this.loggedUserRoleType === "master" || this.loggedUserRoleType === "collaborator"))
    ) {
      this.authorized = true;

      // Set company
      this.companyId = this.cookieService.get("requestedCompany");

      // Init data source
      this.dataSource = new PhonebookDataSource(this.companiesService, this.helperService);

      // Subscribe to listsService and update this list settings
      this.subscribeToListsSettings('phonebook');

    } else {
      this.toastrService.error(this.translateService.instant('common.errors.not_authorized'));
      this.router.navigate([this.lang, 'app']);
      return;
    }
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.firstNameFilter.valueChanges.pipe(startWith(null as string)),
        this.lastNameFilter.valueChanges.pipe(startWith(null as string)),
        this.emailFilter.valueChanges.pipe(startWith(null as string)),
        this.phoneFilter.valueChanges.pipe(startWith(null as string)),
        this.mobileFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
        this.propertyFilter.valueChanges.pipe(startWith(null as string)),
        this.typeFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            // Handle property filter
            if (res) {
              this.propertyIsLoading = true;
              this.getAvailableProperties({
                general: this.propertyFilter.value
              });
            }

            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.paginator.pageIndex = 0;
              this.loadPage();
            }
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    if (this.authorized) {
      this.listsService.setListSetting(
        'phonebook',
        this.paginator.pageIndex.valueOf(),
        this.paginator.pageSize.valueOf(),
        {
          general: '', // this.input.nativeElement.value,
          firstName: this.firstNameFilter.value,
          lastName: this.lastNameFilter.value,
          email: this.emailFilter.value,
          phone: this.phoneFilter.value,
          mobile: this.mobileFilter.value,
          createdAt: this.createdAtFilter.value,
          propertyId: this.propertyFilter.value ? this.propertyFilter.value._id : undefined,
          type: this.typeFilter.value,
        }
      );
    }
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    // this.cardIsLoading = true;
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      firstName: this.firstNameFilter.value,
      lastName: this.lastNameFilter.value,
      email: this.emailFilter.value,
      phoneName: this.phoneFilter.value,
      mobileName: this.mobileFilter.value,
      createdAt: this.createdAtFilter.value ? new Date(this.createdAtFilter.value).toISOString() : undefined,
      propertyId: this.propertyFilter.value ? this.propertyFilter.value._id : undefined,
      type: this.typeFilter.value,
    };

    if (this.loggedUserRoleType === "admin" || this.loggedUserRoleType === "operator") {
      this.dataSource.loadItemsAdmin(start, length, this.sort.active, this.sort.direction, search);
    }
    if (this.loggedUserRoleType === "master" || this.loggedUserRoleType === "collaborator") {
      this.dataSource.loadItems(this.companyId, start, length, this.sort.active, this.sort.direction, search);
    }

    this.subscriptions.push(this.dataSource.getItems().subscribe(res => this.phonebook = res));
    // this.cardIsLoading = false;
  }

  /**
   * Export phonebook elements in a file with given format
   *
   * @since 1.0.0
   *
   * @param format The format. Can be 'excel' or 'csv'
   */

  //  @todo: rivedere e testare
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('companies.fields.phonebook.exportFileName')} ${date}`;
    if (this.loggedUserRoleType === "admin" || this.loggedUserRoleType === "operator") {
      this.subscriptions.push(
        this.companiesService.getExportPhonebookAdmin().subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    } else {
      this.subscriptions.push(
        this.companiesService.getExportPhonebook(this.companyId).subscribe(
          res => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: 'application/vnd.ms-excel'
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.firstNameFilter.setValue(null);
    this.lastNameFilter.setValue(null);
    this.emailFilter.setValue(null);
    this.phoneFilter.setValue(null);
    this.mobileFilter.setValue(null);
    this.createdAtFilter.setValue(null);
    this.propertyFilter.setValue(null);
    this.typeFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = 'desc';
    this.sort.active = 'createdAt';
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.firstName) {
                  this.firstNameFilter.setValue(setting.search.firstName);
                }
                if (setting.search && setting.search.lastName) {
                  this.lastNameFilter.setValue(setting.search.lastName);
                }
                if (setting.search && setting.search.email) {
                  this.emailFilter.setValue(setting.search.email);
                }
                if (setting.search && setting.search.phone) {
                  this.phoneFilter.setValue(setting.search.phone);
                }
                if (setting.search && setting.search.mobile) {
                  this.mobileFilter.setValue(setting.search.mobile);
                }
                if (setting.search && setting.search.createdAt) {
                  this.createdAtFilter.setValue(setting.search.createdAt);
                }
                if (setting.search && setting.search.propertyFilter) {
                  this.propertyFilter.setValue(setting.search.propertyFilter);
                }
                if (setting.search && setting.search.typeFilter) {
                  this.typeFilter.setValue(setting.search.typeFilter);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Triggers the 'add phonebook' dialog
   *
   * @since 1.0.0
   */
  onAddPhonebookContact(): void {
    if (!this.companyId) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(AddPhonebookContactDialogComponent, {
      minWidth: '60%',
      // width: '500px',
      maxHeight: '98vh',
      data: {
        companyId: this.companyId,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (typeof res !== 'undefined' && res.createdPhonebookContact) {
            this.loadPage();
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Triggers the 'import phonebook' dialog
   *
   * @since 1.0.0
   */
  onImportPhonebookContact(): void {
    if (!this.companyId) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(ImportPhonebookContactDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        companyId: this.companyId,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (typeof res !== 'undefined' && res.createdPhonebookContact) {
            this.loadPage();
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Handle click on "Open" button near to each phonebook item
   * and open a view/edit dialog for clicked phonebook
   *
   * @since 1.0.0
   */
  onOpenPhonebookContact(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(EditPhonebookContactDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        phonebookContact: element,
        companyId: this.companyId,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (res) {
            this.loadPage();
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }


  /**
   * Handle click on "Remove" button near to each phonebook item
   * and delete it
   */
  onRemovePhonebookContact(element: any): void {
    if (!element) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: this.translateService.instant('dialogs.confirm.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.subscriptions.push(
            this.companiesService.deletePhonebookContact(this.companyId, element._id)
              .pipe(
                finalize(() => this.deleteBtnOptions.active = false)
              )
              .subscribe(
                res2 => {
                  this.toastrService.success(res2.message);
                  // Once the user is removed, reload data table
                  this.loadPage();
                  return;
                },
                err => {
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }

  /**
   * Handle new message dialog open
   *
   * @since 1.0.0
   */
  onAddCommunication(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(AddCommunicationDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        recipients: [{
          email: element.email,
          name: `${element.firstName} ${element.lastName}`,
        }],
        phonebook: this.phonebook ? this.phonebook : undefined,
      },
      disableClose: true,
    });

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.toastrService.success(this.translateService.instant('communications.add.sendedSuccessfully'));
        }
      );

    // this.subscriptions.push(newMessageDialogRef.afterClosed()
    //   .subscribe(
    //     res => {
    //       if (typeof res !== 'undefined' && res.sendedMessage) {
    //         this.toastrService.success(this.translateService.instant('communications.add.sendedSuccessfully'))
    //         return;
    //       }
    //     },
    //     err => {
    //       this.helperService.handleError(err);
    //     }
    //   ));
  }
  
  /**
   * Handle click on a selected property in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
   onPropertyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.propertyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
   displayFn(arg?: PropertyInterface): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }
  
  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableProperties(search?: any): void {
    this.subscriptions.push(
      this.propertiesService
        .getPropertiesMaster(0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.propertyIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableProperties = res.data.items;
          }
        })
    );
  }

  
  /**
   * Select/Deselect all contacts 
   *
   * @since 1.0.0
   */
  onToggleSelectAllContacts(): void {
    if (this.phonebook.length <= this.selectedContactsId.length) {
      this.selectedContactsId = [];
      this.selectedContacts = [];
    } else {
      this.phonebook.forEach((contact: any) => {
        this.selectedContactsId.push(contact._id);
        this.selectedContacts.push({
          id: contact._id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
        });
      });
    }
  }

  /**
   * Update contacts array with incoming element
   *
   * @since 1.0.0
   */
   onContactChanged(contact: any, checked: boolean): void {
    if (contact && checked) {
      this.selectedContactsId.push(contact._id);
      this.selectedContacts.push({
        id: contact._id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
      });
    } else if (contact && !checked) {
      const index = this.selectedContactsId.indexOf(contact._id);
      this.selectedContactsId.splice(index, 1);

      const indexContact = this.selectedContacts.findIndex((item: any) => {
        item.id.toString() === contact._id
      });
      if (indexContact > -1) {
        this.selectedContacts.splice(indexContact, 1);
      }
    }
  }

  /**
   * Send email
   *
   * @since 1.0.0
   */
   onSendByEmail() {
    if (!this.selectedContacts.length) {
      this.toastrService.error(
        this.translateService.instant("common.errors.chooseElementFirst")
      );
      return;
    }

     // Open dialog
     const newMessageDialogRef = this.dialog.open(
      AddCommunicationDialogComponent,
      {
        minWidth: "70%",
        maxHeight: "98vh",
        data: {
          recipients: this.selectedContacts,
        },
        disableClose: true
      }
    );

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(
      newMessageDialogRef.afterClosed().subscribe(
        res => {
          if (typeof res !== "undefined" && res.sendedMessage) {
            this.toastrService.success(
              this.translateService.instant(
                "communications.add.sendedSuccessfully"
              )
            );
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
   }
}
